import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Instagram, Mail, MapPin, Phone, Globe } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { QuoteForm, BookingForm } from "@/components/ContactBooking";
import { Reveal, Overline } from "@/components/Reveal";
import { DIRECTORS, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_LINK } from "@/data/content";
import { useLang } from "@/i18n";

export default function Contact() {
  const [params] = useSearchParams();
  const initialService = params.get("prestation") || "";
  const initialMessage = params.get("resume") || "";
  const [tab, setTab] = useState("devis");
  const { t, lang } = useLang();

  return (
    <div>
      <Nav />
      <main className="pt-36 pb-28 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-14">
            <Reveal className="lg:col-span-4">
              <Overline>{t("ov.contact")}</Overline>
              <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight mb-6">
                {t("ct.h1")}
              </h1>
              <p className="text-white/80 leading-relaxed mb-5">
                {t("cb.lead")}
              </p>
              <p className="text-white/60 leading-relaxed mb-10 text-sm">
                {t("ct.para")}
              </p>
              <div className="space-y-5 text-sm mb-10">
                <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link"
                  className="flex items-center gap-3 text-white/60 hover:text-gold transition-colors duration-300">
                  <Mail size={16} className="text-gold shrink-0" /> {CONTACT_EMAIL}
                </a>
                <a href={CONTACT_PHONE_LINK} data-testid="contact-phone-link"
                  className="flex items-center gap-3 text-white/60 hover:text-gold transition-colors duration-300">
                  <Phone size={16} className="text-gold shrink-0" /> {CONTACT_PHONE}
                </a>
                <p className="flex items-center gap-3 text-white/60">
                  <MapPin size={16} className="text-gold shrink-0" /> Paris · France
                </p>
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-4">{t("ct.team")}</p>
                <div className="space-y-4">
                  {DIRECTORS.map((d) => (
                    <div key={d.handle} className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-white/40 w-36">
                        {lang === "en" ? t(d.handle === "@artwhos" ? "st.role1" : "st.role2") : d.role}
                      </span>
                      <a href={`https://instagram.com/${d.handle.slice(1)}`} target="_blank" rel="noopener noreferrer"
                        data-testid={`contact-instagram-${d.handle.slice(1)}`}
                        className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs text-white/60 transition-colors duration-300 hover:border-gold hover:text-gold">
                        <Instagram size={13} /> {d.handle}
                      </a>
                      <a href={d.siteUrl} target="_blank" rel="noopener noreferrer"
                        data-testid={`contact-site-${d.handle.slice(1)}`}
                        className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs text-white/60 transition-colors duration-300 hover:border-gold hover:text-gold">
                        <Globe size={13} /> {d.site}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-12" role="tablist">
                <button data-testid="tab-devis" role="tab" aria-selected={tab === "devis"}
                  onClick={() => setTab("devis")}
                  className={`px-6 py-3 text-sm uppercase tracking-[0.15em] border transition-colors duration-300 ${
                    tab === "devis" ? "border-gold bg-gold text-black" : "border-white/20 text-white/60 hover:border-gold/60 hover:text-gold"
                  }`}>
                  {t("cb.tab1")}
                </button>
                <button data-testid="tab-booking" role="tab" aria-selected={tab === "booking"}
                  onClick={() => setTab("booking")}
                  className={`px-6 py-3 text-sm uppercase tracking-[0.15em] border transition-colors duration-300 ${
                    tab === "booking" ? "border-gold bg-gold text-black" : "border-white/20 text-white/60 hover:border-gold/60 hover:text-gold"
                  }`}>
                  {t("cb.tab2")}
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-8">
              {tab === "devis" ? <QuoteForm initialService={initialService} initialMessage={initialMessage} /> : <BookingForm />}
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
